import { DrawerName, IDrawerType } from '@src/framework/workbench/drawer/constants';
import { DrawerService, InjectorService } from '@tencent/workbench';
import { DRAWER_PROPERTY_EDITOR_RIGHT_MARGIN, DRAWER_PROPERTY_EDITOR_WIDTH, MAX_DRAWER_PROPERTY_EDITOR_WIDTH, STATUS_BAR_HEIGHT } from '@editor/src/js/constants';
import { SelectWindowShowAction } from '@src/js/constant';
import { observable } from 'mobx';
import { IDrawerStateManager, IDrawerStateManagerServiceDelegates } from './drawer-state-manager.interface';
import { PPTEditorStateEnumType } from '../../edit-extension/edit-extension-export';
import QuickSettingManager from '@src/framework/frame/components/quick-setting/quick-setting-manager';

// 记录所有用到wb drawer的侧边栏列表
const DRAWER_NAME_LIST = Object.keys(DrawerName);
// TODO 目前的statusbar接在编辑区会被遮挡
export const DRAWER_HEIGHT = '100%';

export class DrawerStateManager implements IDrawerStateManager {
    /**
     * 侧边栏菜单是否展示;
     */
    @observable
    public isDrawerMenuShown = false;

    @observable
    public openedDrawers: IDrawerType[] = [];

    @observable
    public currentDrawer: IDrawerType | null;

    private drawerService: DrawerService;
    private showHolderTimeout;
    private showDrawerTimeout;

    private services: IDrawerStateManagerServiceDelegates;

    public setServiceDelegates(services: IDrawerStateManagerServiceDelegates) {
        this.services = {
            ...this.services,
            ...services,
        };
        this.drawerService = InjectorService.get(DrawerService);
    }

    public init() {
        this.updateDrawerStyle();
        this.registerRightSideBarCallback();
    }

    // 解决协同场景，如果除name之外无其他侧边栏打开，则关闭侧边栏
    public closeRightDrawer = (type: string) => {
        let hasDrawerOpened = false;
        const drawerList = [
            {
                type: DrawerName.SLIDE_PROPERTY_DRAWER,
                state: this.drawerService.isRightOpening(DrawerName.SLIDE_PROPERTY_DRAWER),
            },
            {
                type: DrawerName.ANIMATION_RIGHT_DRAWER,
                state: this.drawerService.isRightOpening(DrawerName.ANIMATION_RIGHT_DRAWER),
            },
            {
                type: DrawerName.SLIDE_RATING_DRAWER,
                state: this.drawerService.isRightOpening(DrawerName.SLIDE_RATING_DRAWER),
            },
            {
                type: DrawerName.SELECT_WINDOW_RIGHT_DRAWER,
                state: this.drawerService.isRightOpening(DrawerName.SELECT_WINDOW_RIGHT_DRAWER),
            },
            {
                type: DrawerName.COMMENT_DRAWER,
                state: this.drawerService.isRightOpening(DrawerName.COMMENT_DRAWER),
            },
            {
                type: DrawerName.MACHINE_AUDIT,
                state: this.drawerService.isRightOpening(DrawerName.MACHINE_AUDIT),
            },
        ];
        drawerList.forEach((item) => {
            if (item.type !== type) {
                hasDrawerOpened = item.state || hasDrawerOpened;
            }
        });
        if (!hasDrawerOpened) {
            this.handleDrawerHolderVisiable(false);
            // 插件使用的也是wb的drawer，因此还需要判断下是否有其他drawer打开
            if (!this.drawerService.isRightOpening(type) && this.drawerService.isRightOpening()) {
                return;
            }
            this.drawerService.closeRightDrawer();
        }
    };

    // 直接关闭右侧抽屉，可以配置无需关闭的白名单
    public closeRightDrawerDirectly(whiteList: string[]) {
        if (whiteList.some(name => this.drawerService.isRightOpening(name))) {
            return;
        }
        this.drawerService.closeRightDrawer();
    }

    /**
     * drawer占位，解决drawer滚动事件传递到canvas-wrapper的问题
     */
    public handleDrawerHolderVisiable = (visiable: boolean) => {
        const drawerHolderDom = document.getElementById('slide-drawer-holder');
        if (drawerHolderDom) {
            if (visiable) {
                this.showHolderTimeout = setTimeout(() => {
                    drawerHolderDom.style.display = 'flex';
                    drawerHolderDom.style.width = `${this.getDrawerWidth()}px`;
                    clearTimeout(this.showHolderTimeout);
                    // drawer 更新的时候同步更新悬浮菜单的位置
                    QuickSettingManager.getInstance().updateAnchorRect();
                }, 100);
            } else {
                drawerHolderDom.style.display = 'none';
            }
        }
    };

    public updateDrawerHolderStyle = () => {
        const drawerHolderDom = document.getElementById('slide-drawer-holder');
        if (drawerHolderDom) {
            drawerHolderDom.style.width = `${this.getDrawerWidth()}px`;
        }
    };

    /**
     * 清除记录的从格式面板打开的selectWindow的状态
     */
    public resetSelectWindowActions = () => {
        if (this.services.rightSideBarManager) {
            this.services.rightSideBarManager.setSelectWindowShowAction(SelectWindowShowAction.NONE);
        }
    };

    /**
     * 获取侧边栏宽度
     */
    public getDrawerWidth = (): number => (this.drawerService?.isRightOpening(DrawerName.SELECT_WINDOW_RIGHT_DRAWER)
        ? this.services.rightSideBarManager.selectWindowWidth + DRAWER_PROPERTY_EDITOR_RIGHT_MARGIN
        : DRAWER_PROPERTY_EDITOR_WIDTH + DRAWER_PROPERTY_EDITOR_RIGHT_MARGIN);

    // 部分addon用到了drawer，打开后需要处理比如关闭其他RightSideBar
    public handleAddonDrawerAfterOpened = () => {
        clearTimeout(this.showDrawerTimeout);
        this.showDrawerTimeout = setTimeout(() => {
            let open = this.drawerService.isRightOpening();
            DRAWER_NAME_LIST.forEach((item) => {
                // 避免打开的侧边栏是如格式面板等
                if (this.drawerService.isRightOpening(item)) {
                    open = false;
                }
            });
            if (open) {
                this.resetSelectWindowActions();
                this.services.rightSideBarService.hide();
            }
        }, 50);
    };

    public openTab(drawer: IDrawerType) {
        this.currentDrawer = drawer;
        if (this.openedDrawers.indexOf(drawer) > -1) {
            return;
        }
        this.openedDrawers.push(drawer);
    }

    public closeTab(drawer: IDrawerType) {
        const index = this.openedDrawers.indexOf(drawer);
        if (index === -1) {
            return;
        }
        this.openedDrawers.splice(index, 1);
        this.currentDrawer = this.openedDrawers[Math.max(index - 1, 0)];
        this.openCurrentDrawer();
    }

    public closeAllTab(whiteList: string[]) {
        if (this.currentDrawer && whiteList.some(name => name === this.currentDrawer)) {
            this.openedDrawers.length = 0;
            this.openedDrawers.push(this.currentDrawer);
        } else {
            this.openedDrawers.length = 0;
            this.currentDrawer = null;
        }
    }

    /**
     * 影响编辑区大小的侧边栏是否有展示
     */
    public isResizeDrawerShown = (): boolean =>
        [
            this.drawerService?.isRightOpening(DrawerName.SLIDE_PROPERTY_DRAWER),
            this.drawerService?.isRightOpening(DrawerName.ANIMATION_RIGHT_DRAWER),
            this.drawerService?.isRightOpening(DrawerName.SELECT_WINDOW_RIGHT_DRAWER),
            this.drawerService?.isRightOpening(DrawerName.SLIDE_RATING_DRAWER),
            this.drawerService?.isRightOpening(DrawerName.REPLACE_IMAGE),
            this.drawerService?.isRightOpening(DrawerName.COMMENT_DRAWER),
            this.drawerService?.isRightOpening(DrawerName.MACHINE_AUDIT),
        ].some(Boolean);

    // TODO 目前的statusbar接在编辑区会被遮挡
    private updateDrawerStyle = () => {
        const rightDrawerContainer = document.getElementById('workbench-drawer-right')?.parentElement;
        if (rightDrawerContainer?.style) {
            rightDrawerContainer.style.bottom = `${STATUS_BAR_HEIGHT}px`;
        }
    };

    /**
     * 初始化文件信息等侧边栏的回调，打开前需要关闭除了格式面板之外的其他面板
     */
    private registerRightSideBarCallback = () => {
        this.services.rightSideBarService.registerCallback({
            beforeShow: () => {
                this.closeRightDrawerDirectly([
                    DrawerName.SLIDE_PROPERTY_DRAWER,
                    DrawerName.ANIMATION_RIGHT_DRAWER,
                    DrawerName.COMMENT_DRAWER,
                    DrawerName.SELECT_WINDOW_RIGHT_DRAWER,
                ]);
            },
            afterHide: () => {
                this.openCurrentDrawer();
            },
        });

        // 关闭侧边栏-动画、格式
        this.services.userPrivilegeService.onPPTEditorStateChange((state: PPTEditorStateEnumType) => {
            if (state === PPTEditorStateEnumType.EDITABLE) return;
            this.closeRightDrawerDirectly([DrawerName.AUTH, DrawerName.COMMENT_DRAWER]);
        });
    };

    private openCurrentDrawer = (): void => {
        if (this.openedDrawers.length <= 0) {
            return;
        }
        switch (this.currentDrawer) {
            case DrawerName.SLIDE_PROPERTY_DRAWER:
                this.services.rightSideBarManager.setIsPropertyEditorShown(true);
                break;
            case DrawerName.ANIMATION_RIGHT_DRAWER:
                this.services.rightSideBarManager.setIsAnimationEditorShown(true);
                break;
            case DrawerName.SELECT_WINDOW_RIGHT_DRAWER:
                this.services.rightSideBarManager.setIsSelectWindowShown(true);
                break;
            case DrawerName.COMMENT_DRAWER:
                this.services.commentManager.promise().then((commentManager) => {
                    commentManager.setIsCommentDrawerShown(true);
                });
                break;
        }
    };
}
